import { useState, useEffect, useRef, useCallback } from 'react';
import type { Toast as ToastData } from '../../store/notifications';


// ── Icons par type ──

const icons: Record<ToastData['type'], JSX.Element> = {
  success: (
    <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  error: (
    <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
  ),
  warning: (
    <svg className="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
    </svg>
  ),
  info: (
    <svg className="w-5 h-5 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
    </svg>
  ),
  alert: (
    <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
    </svg>
  ),
};

const bgColors: Record<ToastData['type'], string> = {
  success: 'border-emerald-500/30 bg-emerald-500/5',
  error: 'border-red-500/30 bg-red-500/5',
  warning: 'border-amber-500/30 bg-amber-500/5',
  info: 'border-cyan-500/30 bg-cyan-500/5',
  alert: 'border-purple-500/30 bg-purple-500/5',
};

const progressColors: Record<ToastData['type'], string> = {
  success: 'bg-emerald-400',
  error: 'bg-red-400',
  warning: 'bg-amber-400',
  info: 'bg-cyan-400',
  alert: 'bg-purple-400',
};

interface ToastProps {
  toast: ToastData;
  onRemove: () => void;
}

export default function Toast({ toast, onRemove }: ToastProps) {
  const [expanded, setExpanded] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [progress, setProgress] = useState(100);
  const [paused, setPaused] = useState(false);
  const duration = toast.duration ?? 5000;
  const startRef = useRef(Date.now());
  const remainingRef = useRef(duration);
  const rafRef = useRef<number | null>(null);

  const handleClose = useCallback(() => {
    setExiting(true);
    setTimeout(onRemove, 300);
  }, [onRemove]);

  // Progress bar animation
  useEffect(() => {
    if (paused) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }

    startRef.current = Date.now();
    const tick = () => {
      const elapsed = Date.now() - startRef.current;
      const left = remainingRef.current - elapsed;
      if (left <= 0) {
        setProgress(0);
        handleClose();
        return;
      }
      setProgress((left / duration) * 100);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [paused, duration, handleClose]);

  // Update remaining when pausing
  const handleMouseEnter = () => {
    const elapsed = Date.now() - startRef.current;
    remainingRef.current = Math.max(0, remainingRef.current - elapsed);
    setPaused(true);
  };

  const handleMouseLeave = () => {
    setPaused(false);
  };

  const isLongMessage = toast.message.length > 80;
  const timeStr = new Date(toast.createdAt).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div
      role="alert"
      aria-live="polite"
      aria-atomic="true"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`
        relative w-80 border rounded-lg shadow-2xl backdrop-blur-md overflow-hidden
        transition-all duration-300 ease-out
        ${bgColors[toast.type]}
        ${exiting ? 'translate-x-full opacity-0' : 'translate-x-0 opacity-100'}
      `}
      style={{ animation: exiting ? undefined : 'slideInRight 0.3s ease-out' }}
    >
      {/* Content */}
      <div className="p-3">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">{icons[toast.type]}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold text-gray-100 truncate">{toast.title}</p>
              <span className="text-[10px] text-gray-500 flex-shrink-0">{timeStr}</span>
            </div>
            <p
              className={`text-xs text-gray-400 mt-0.5 ${
                !expanded && isLongMessage ? 'line-clamp-2 cursor-pointer' : ''
              }`}
              onClick={() => isLongMessage && setExpanded(!expanded)}
            >
              {toast.message}
            </p>
            {isLongMessage && !expanded && (
              <button
                onClick={() => setExpanded(true)}
                className="text-[10px] text-cyan-400 hover:underline mt-0.5"
              >
                Voir plus
              </button>
            )}
            {toast.action && (
              <button
                onClick={toast.action.onClick}
                className="mt-2 px-2.5 py-1 text-xs font-medium rounded bg-white/10 hover:bg-white/15 text-gray-200 transition-colors"
              >
                {toast.action.label}
              </button>
            )}
          </div>
          {/* Close button */}
          <button
            onClick={handleClose}
            aria-label="Fermer la notification"
            className="flex-shrink-0 p-0.5 rounded hover:bg-white/10 text-gray-500 hover:text-gray-300 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-0.5 w-full bg-white/5">
        <div
          className={`h-full ${progressColors[toast.type]} transition-none`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}
